import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Tooltip } from '@mui/material';
import productoService, { OPTIONS_PRODUCTO_OPERATIVO } from '../../services/productoService';
import RecetaArticuloCodigoField from '../../pages/produccion/RecetaArticuloCodigoField';
import RecetaArticuloSearchDialog, {
  RecetaArticuloSeleccionado,
} from '../../pages/produccion/RecetaArticuloSearchDialog';

interface Props {
  label: string;
  value: string;
  disabled?: boolean;
  size?: 'small' | 'medium';
  dialogTitle?: string;
  /** Debounce de sugerencias (ms). */
  debounceMs?: number;
  onChange: (codigo: string) => void;
}

const debeResolverCodigo = (codigo: string): boolean => {
  const trimmed = codigo.trim();
  return trimmed.length > 0 && !trimmed.includes(' ');
};

const GranjaArticuloCodigoField: React.FC<Props> = ({
  label,
  value,
  disabled,
  size = 'small',
  dialogTitle,
  debounceMs = 150,
  onChange,
}) => {
  const [codigoInput, setCodigoInput] = useState(value);
  const [descripcion, setDescripcion] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const applyRequestIdRef = useRef(0);
  const hintRequestIdRef = useRef(0);

  useEffect(() => {
    setCodigoInput(value);
  }, [value]);

  useEffect(() => {
    const code = value.trim();
    if (!code) {
      setDescripcion('');
      return;
    }

    const requestId = ++hintRequestIdRef.current;
    (async () => {
      try {
        const producto = await productoService.getProducto(code, OPTIONS_PRODUCTO_OPERATIVO);
        if (requestId !== hintRequestIdRef.current) return;
        setDescripcion(String(producto.descripcion ?? '').trim());
      } catch {
        if (requestId !== hintRequestIdRef.current) return;
        setDescripcion('');
      }
    })();
  }, [value]);

  const articuloResuelto =
    value.trim().length > 0 &&
    codigoInput.trim().toUpperCase() === value.trim().toUpperCase();

  const aplicarProducto = useCallback(
    async (codigo: string) => {
      const trimmed = codigo.trim();
      const requestId = ++applyRequestIdRef.current;

      if (!trimmed) {
        onChange('');
        setDescripcion('');
        setError(null);
        return;
      }

      setResolving(true);
      setError(null);
      try {
        const producto = await productoService.getProducto(trimmed, OPTIONS_PRODUCTO_OPERATIVO);
        if (requestId !== applyRequestIdRef.current) return;

        const codigoOk = producto.codigoArticulo.trim();
        setCodigoInput(codigoOk);
        setDescripcion(String(producto.descripcion ?? '').trim());
        onChange(codigoOk);
      } catch {
        if (requestId !== applyRequestIdRef.current) return;
        setDescripcion('');
        setError(`No se encontró el artículo ${trimmed}.`);
      } finally {
        if (requestId === applyRequestIdRef.current) setResolving(false);
      }
    },
    [onChange],
  );

  const handleSelect = (item: RecetaArticuloSeleccionado) => {
    applyRequestIdRef.current += 1;
    setError(null);
    setCodigoInput(item.codigoArticulo);
    void aplicarProducto(item.codigoArticulo);
  };

  const handleResolveExact = (codigo: string) => {
    const trimmed = codigo.trim();
    if (!debeResolverCodigo(trimmed)) return;
    if (value.trim().length > 0 && trimmed.toUpperCase() === value.trim().toUpperCase()) {
      return;
    }
    void aplicarProducto(trimmed);
  };

  const tooltipTitle =
    !error && articuloResuelto && descripcion
      ? `${value.trim()} — ${descripcion}`
      : '';

  return (
    <>
      <Tooltip title={tooltipTitle} placement="top" enterDelay={400}>
        <span style={{ display: 'block', width: '100%' }}>
          <RecetaArticuloCodigoField
            label={label}
            size={size}
            value={codigoInput}
            articuloResuelto={articuloResuelto}
            resolving={resolving}
            disabled={disabled}
            error={Boolean(error)}
            helperText={error || undefined}
            debounceMs={debounceMs}
            modoPrecio="costo"
            sx={{
              m: 0,
              width: '100%',
              '& .MuiFormHelperText-root': { mx: 0 },
              '& input': { fontFamily: 'monospace' },
            }}
            onChange={(raw) => {
              setError(null);
              setCodigoInput(raw);
              if (!raw.trim() && value.trim()) {
                onChange('');
                setDescripcion('');
              } else if (descripcion) {
                setDescripcion('');
              }
            }}
            onSelect={handleSelect}
            onResolveExact={handleResolveExact}
            onOpenDialog={() => setDialogOpen(true)}
          />
        </span>
      </Tooltip>

      <RecetaArticuloSearchDialog
        open={dialogOpen}
        title={dialogTitle ?? label}
        modoPrecio="costo"
        initialSearchTerm={codigoInput}
        onClose={() => setDialogOpen(false)}
        onSelect={handleSelect}
      />
    </>
  );
};

export default GranjaArticuloCodigoField;
